/**
 * ApiKeysSection (v1.0.23) — gestión de API keys para integraciones externas.
 *
 * Sistema:
 *   - Listar keys activas (sin secret, solo prefix)
 *   - Crear nueva key con scopes seleccionables → muestra plain key UNA SOLA VEZ
 *   - Revocar key (soft delete con motivo)
 *
 * Permisos: api_keys.read / api_keys.write
 *
 * Caso de uso principal: ByteNMS consume /api/v1/clientes y /api/v1/routers
 * vía API key con scopes [clientes.read, routers.read].
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Copy,
  KeyRound,
  Loader2,
  Plus,
  ShieldOff,
  Trash2,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiCall } from "@/lib/api";
import { getTz } from "@/lib/tz";

interface ApiKey {
  id: string;
  name: string;
  description: string | null;
  key_prefix: string;
  scopes: string[];
  last_used_at: string | null;
  last_used_ip: string | null;
  use_count: number;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  created_at: string;
  status: "active" | "revoked" | "expired";
}

const AVAILABLE_SCOPES = [
  { code: "clientes.read", label: "Leer clientes" },
  { code: "routers.read", label: "Leer routers" },
  { code: "routers.credentials", label: "Obtener credenciales API del router (sólo integraciones internas)" },
  { code: "contratos.read", label: "Leer contratos" },
  { code: "planes.read", label: "Leer planes" },
  { code: "redes.read", label: "Leer redes" },
];

export function ApiKeysSection() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState<{
    plain_key: string;
    api_key: ApiKey;
  } | null>(null);

  const { data: keys = [], isLoading } = useQuery<ApiKey[]>({
    queryKey: ["api-keys"],
    queryFn: async () => apiCall<ApiKey[]>("get", "/api/v1/api-keys"),
  });

  const revoke = useMutation({
    mutationFn: async (id: string) =>
      apiCall("post", `/api/v1/api-keys/${id}/revoke`, { json: { reason: "Revocada desde panel" } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["api-keys"] });
      toast.success("API key revocada");
    },
    onError: (e: any) => toast.error(e?.message ?? "Error revocando"),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-medium">
            <KeyRound size={14} />
            API Keys
          </h3>
          <p className="text-xs text-muted-foreground">
            Credenciales para integraciones externas (ej. ByteNMS). Cada key
            tiene scopes específicos y se puede revocar individualmente.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus size={12} className="mr-1" />
          Nueva API key
        </Button>
      </div>

      {isLoading ? (
        <div className="text-xs text-muted-foreground">Cargando…</div>
      ) : keys.length === 0 ? (
        <div className="rounded border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
          No hay API keys creadas. Crea una para que sistemas externos como
          ByteNMS puedan consumir tu API.
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-border">
          <table className="w-full text-xs">
            <thead className="border-b border-border bg-muted/30">
              <tr className="text-left">
                <th className="px-3 py-2">Nombre</th>
                <th className="px-3 py-2">Prefix</th>
                <th className="px-3 py-2">Scopes</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Último uso</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {keys.map((k) => (
                <tr key={k.id}>
                  <td className="px-3 py-2 font-medium">{k.name}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{k.key_prefix}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {k.scopes.map((s) => (
                        <span
                          key={s}
                          className="rounded bg-muted px-1.5 py-0.5 text-[10px]"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {k.status === "active" && (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] text-green-800">
                        ● activa
                      </span>
                    )}
                    {k.status === "revoked" && (
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] text-red-800">
                        ● revocada
                      </span>
                    )}
                    {k.status === "expired" && (
                      <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] text-yellow-800">
                        ● expirada
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {k.last_used_at
                      ? new Date(k.last_used_at).toLocaleString("es-MX", {
                          timeZone: getTz(),
                        })
                      : "nunca"}
                    {k.use_count > 0 && (
                      <span className="ml-1 text-[10px]">({k.use_count} usos)</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end">
                      {k.status === "active" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (confirm(`¿Revocar API key "${k.name}"? Esta acción no se puede deshacer.`)) {
                              revoke.mutate(k.id);
                            }
                          }}
                          title="Revocar"
                        >
                          <ShieldOff size={12} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateApiKeyDialog
          onClose={() => setShowCreate(false)}
          onCreated={(result) => {
            setNewKeyResult(result);
            setShowCreate(false);
          }}
        />
      )}

      {newKeyResult && (
        <ShowPlainKeyDialog
          plainKey={newKeyResult.plain_key}
          apiKey={newKeyResult.api_key}
          onClose={() => setNewKeyResult(null)}
        />
      )}
    </div>
  );
}

function CreateApiKeyDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (r: { plain_key: string; api_key: ApiKey }) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scopes, setScopes] = useState<string[]>(["clientes.read", "routers.read"]);

  const create = useMutation({
    mutationFn: async () =>
      apiCall<{ api_key: ApiKey; plain_key: string }>("post", "/api/v1/api-keys", {
        json: {
          name,
          description: description || null,
          scopes,
        },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["api-keys"] });
      onCreated(r);
    },
    onError: (e: any) => toast.error(e?.message ?? "Error creando"),
  });

  const toggleScope = (code: string) =>
    setScopes((prev) =>
      prev.includes(code) ? prev.filter((s) => s !== code) : [...prev, code]
    );

  return (
    <Modal title="Nueva API key" onClose={onClose}>
      <div className="space-y-3 text-xs">
        <div>
          <Label>Nombre</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ByteNMS — integración monitoreo"
            autoFocus
          />
        </div>
        <div>
          <Label>Descripción (opcional)</Label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Para que ByteNMS lea clientes y routers"
          />
        </div>
        <div>
          <Label>Scopes (permisos)</Label>
          <div className="mt-1 flex flex-col gap-1.5">
            {AVAILABLE_SCOPES.map((s) => (
              <label
                key={s.code}
                className="flex cursor-pointer items-center gap-2 rounded border border-border p-2 hover:bg-muted/50"
              >
                <input
                  type="checkbox"
                  checked={scopes.includes(s.code)}
                  onChange={() => toggleScope(s.code)}
                />
                <span className="font-mono text-[11px]">{s.code}</span>
                <span className="text-muted-foreground">— {s.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => create.mutate()}
            disabled={create.isPending || name.length < 2 || scopes.length === 0}
          >
            {create.isPending && <Loader2 size={12} className="mr-1 animate-spin" />}
            Crear API key
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ShowPlainKeyDialog({
  plainKey,
  apiKey,
  onClose,
}: {
  plainKey: string;
  apiKey: ApiKey;
  onClose: () => void;
}) {
  return (
    <Modal title="API key creada" onClose={onClose}>
      <div className="space-y-3 text-xs">
        <div className="rounded border border-yellow-300 bg-yellow-50 p-3 text-yellow-900">
          ⚠️ <strong>GUÁRDALA AHORA.</strong> No volveremos a mostrar este valor.
          Si la pierdes, tendrás que crear una nueva.
        </div>

        <div>
          <Label>Nombre</Label>
          <div className="font-mono text-[11px]">{apiKey.name}</div>
        </div>

        <div>
          <Label>Scopes</Label>
          <div className="flex flex-wrap gap-1">
            {apiKey.scopes.map((s) => (
              <span key={s} className="rounded bg-muted px-1.5 py-0.5 text-[10px]">
                {s}
              </span>
            ))}
          </div>
        </div>

        <div>
          <Label>Clave (úsala en header X-API-Key)</Label>
          <div className="flex gap-2">
            <code className="flex-1 break-all rounded border border-border bg-muted/50 p-2 font-mono text-[11px]">
              {plainKey}
            </code>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                navigator.clipboard.writeText(plainKey);
                toast.success("Copiada al portapapeles");
              }}
            >
              <Copy size={12} />
            </Button>
          </div>
        </div>

        <div className="rounded border border-border bg-muted/30 p-2 text-[11px]">
          <div className="font-medium">Ejemplo de uso:</div>
          <code className="mt-1 block break-all font-mono">
            curl -H "X-API-Key: {plainKey.slice(0, 20)}..." https://io.bytecom.mx/api/v1/clientes
          </code>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={onClose}>Listo, ya la guardé</Button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-border bg-background p-4 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold">{title}</h3>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X size={14} />
          </Button>
        </header>
        {children}
      </div>
    </div>
  );
}
